import { useLocation } from "react-router";

/**
 * Router state carried into a detail page so it can label its back control with
 * the list you came from. Stored by react-router under `window.history.state.usr`,
 * so unlike an in-memory tracker it survives a full page reload.
 */
export type FromState = { from: string };

export const isFromState = (state: unknown): state is FromState =>
  typeof state === "object" && state !== null && typeof (state as FromState).from === "string";

/**
 * The `state` to attach when navigating into a detail page:
 * `<Link to={href} state={from} />` or `navigate(href, { state: from })`.
 */
export function useFromState(): FromState {
  const { pathname, search } = useLocation();
  return { from: pathname + search };
}

/**
 * True when there is an earlier entry in this tab's history that the router
 * itself pushed — i.e. `navigate(-1)` returns to somewhere inside the app
 * rather than leaving it.
 *
 * react-router's browser history keeps a running `idx` in `window.history.state`
 * (see `getHistoryState` in react-router/lib/router/history), starting at 0 on a
 * cold load. A missing `idx` yields `false`, which degrades to the caller's
 * fallback path rather than a dead-end button.
 *
 * Reads live browser state, so components should memoise it on `location.key`
 * to recompute exactly when history moves rather than on every render.
 */
export function canGoBack(): boolean {
  if (typeof window === "undefined") return false;
  const idx = (window.history.state as { idx?: number } | null)?.idx;
  return typeof idx === "number" && idx > 0;
}
